import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { uid } from '@/lib/readiness/util'
import { seedCapabilities, seedSharedUpdates, seedTechnologies } from '@/lib/catalogue/data'
import type { CatalogueCapability, CatalogueSuggestion, CatalogueTechnology, CatalogueUpdate, Capability, UpdateKind } from '@/api/types/catalogue'

// ── Catalogue store (I3 §9.3) ───────────────────────────────────────────────
// The shared catalogue (technologies + capabilities + Quantizant's history)
// and, keyed by org, the organisation's own updates, suggested changes and
// unmatched-name mappings. Persisted under `qtz-catalogue`. No organisation
// ever reads another organisation's rows: every org-scoped selector filters
// by org_id. Shared fields are written only by Quantizant (`canCurate`) or,
// for an entry an organisation added itself, by that organisation.

const now = () => new Date().toISOString()

export interface CapabilityChange {
  technology: CatalogueTechnology
  capability: Capability
  before: CatalogueCapability | null
  after: CatalogueCapability
}

interface CatalogueStore {
  technologies: CatalogueTechnology[]
  capabilities: CatalogueCapability[]
  updates: CatalogueUpdate[] // shared (org_id null) and org-scoped
  suggestions: CatalogueSuggestion[]
  mappings: Record<string, Record<string, string>> // org → detected name → technology id
  item_links: Record<string, Record<string, string[]>> // org → technology id → migration item ids linked by hand
  addTechnology: (input: Omit<CatalogueTechnology, 'id' | 'created_at' | 'updated_at' | 'vendor_id'>, caps: Array<Omit<CatalogueCapability, 'technology_id'>>, actor: string) => CatalogueTechnology
  updateTechnology: (id: string, patch: Partial<Pick<CatalogueTechnology, 'name' | 'vendor' | 'category' | 'description' | 'versions' | 'logo_asset'>>, actor: string) => void
  // Returns the change so the caller can fan it out (items, events, notifications).
  setCapability: (techId: string, cap: Capability, patch: Partial<Omit<CatalogueCapability, 'technology_id' | 'capability'>>, actor: string, sharedNote?: string) => CapabilityChange | null
  logUpdate: (input: { technology_id: string; org_id: string | null; kind: UpdateKind; text: string }, actor: string) => CatalogueUpdate
  suggestChange: (input: Omit<CatalogueSuggestion, 'id' | 'created_at' | 'status'>) => CatalogueSuggestion
  mapUnmatched: (orgId: string, name: string, techId: string) => void
  linkItem: (orgId: string, techId: string, itemId: string) => void
  resetCatalogue: () => void
}

const seeded = () => ({ technologies: seedTechnologies(), capabilities: seedCapabilities(), updates: seedSharedUpdates(), suggestions: [] as CatalogueSuggestion[], mappings: {} as Record<string, Record<string, string>>, item_links: {} as Record<string, Record<string, string[]>> })

export const useCatalogueStore = create<CatalogueStore>()(
  persist(
    (set, get) => ({
      ...seeded(),

      addTechnology: (input, caps, actor) => {
        const at = now()
        const tech: CatalogueTechnology = { ...input, id: uid('tech'), vendor_id: `vnd-${input.vendor.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`, created_at: at, updated_at: at, updated_by: actor }
        set((st) => ({
          technologies: [...st.technologies, tech],
          capabilities: [...st.capabilities, ...caps.map((c) => ({ ...c, technology_id: tech.id }))],
          updates: [...st.updates, { id: uid('cu'), technology_id: tech.id, org_id: input.maintained_by === 'quantizant' ? null : input.maintained_by, kind: 'detail', text: 'Entry added', author: actor, created_at: at }],
        }))
        return tech
      },

      updateTechnology: (id, patch, actor) =>
        set((st) => ({ technologies: st.technologies.map((t) => (t.id === id ? { ...t, ...patch, updated_at: now(), updated_by: actor } : t)) })),

      setCapability: (techId, cap, patch, actor, sharedNote) => {
        const tech = get().technologies.find((t) => t.id === techId)
        if (!tech) return null
        const before = get().capabilities.find((c) => c.technology_id === techId && c.capability === cap) ?? null
        const at = now()
        const after: CatalogueCapability = {
          ...(before ?? { technology_id: techId, capability: cap, status: 'unknown', algorithms: [], hybrid: false, from_version: null, roadmap_date: null, roadmap_confidence: null, source_url: null, source_note: null, verified_at: null, verified_by: null }),
          ...patch,
        }
        const moved = before?.roadmap_date !== after.roadmap_date || before?.status !== after.status
        set((st) => ({
          capabilities: before ? st.capabilities.map((c) => (c === before ? after : c)) : [...st.capabilities, after],
          technologies: st.technologies.map((t) => (t.id === techId ? { ...t, updated_at: at, updated_by: actor } : t)),
          updates: moved
            ? [...st.updates, { id: uid('cu'), technology_id: techId, org_id: tech.maintained_by === 'quantizant' ? null : tech.maintained_by, kind: before?.roadmap_date !== after.roadmap_date ? 'roadmap_changed' : 'detail', text: sharedNote || describeChange(before, after), author: actor, created_at: at }]
            : st.updates,
        }))
        return moved ? { technology: tech, capability: cap, before, after } : null
      },

      logUpdate: (input, actor) => {
        const u: CatalogueUpdate = { ...input, id: uid('cu'), author: actor, created_at: now() }
        set((st) => ({ updates: [...st.updates, u] }))
        return u
      },

      suggestChange: (input) => {
        const s: CatalogueSuggestion = { ...input, id: uid('sug'), created_at: now(), status: 'pending' }
        set((st) => ({ suggestions: [...st.suggestions, s] }))
        return s
      },

      mapUnmatched: (orgId, name, techId) => set((st) => ({ mappings: { ...st.mappings, [orgId]: { ...(st.mappings[orgId] ?? {}), [name.toLowerCase()]: techId } } })),

      linkItem: (orgId, techId, itemId) =>
        set((st) => {
          const cur = st.item_links[orgId]?.[techId] ?? []
          return cur.includes(itemId) ? st : { item_links: { ...st.item_links, [orgId]: { ...(st.item_links[orgId] ?? {}), [techId]: [...cur, itemId] } } }
        }),

      resetCatalogue: () => set(seeded()),
    }),
    { name: 'qtz-catalogue', version: 1 }
  )
)

function describeChange(before: CatalogueCapability | null, after: CatalogueCapability): string {
  const label = after.capability.replace(/_/g, ' ')
  if (before?.roadmap_date !== after.roadmap_date) return `Roadmap for ${label} moved ${before?.roadmap_date ?? '—'} → ${after.roadmap_date ?? '—'}`
  return `Status for ${label} ${before?.status ?? 'unknown'} → ${after.status}`
}

// Org-scoped reads: shared history plus this organisation's own rows only.
export const updatesFor = (updates: CatalogueUpdate[], techId: string, orgId: string) => updates.filter((u) => u.technology_id === techId && (u.org_id === null || u.org_id === orgId)).sort((a, b) => b.created_at.localeCompare(a.created_at))
export const visibleTechnologies = (techs: CatalogueTechnology[], orgId: string) => techs.filter((t) => t.maintained_by === 'quantizant' || t.maintained_by === orgId)
